import type { SessionContextPluginState } from '../../ai-session-context/types';
import type { PurePmInEditorDirectStreamingPluginState } from '../types';

export type StreamingActionType = 'mount' | 'update' | 'finalize' | 'discard' | 'cleanup';

/**
 * Determines what streaming action (if any) should be taken based on session state changes.
 *
 * Actions in priority order:
 * - cleanup: Session changed while we were actively streaming → reset state first
 * - finalize: Stream just completed (status: streaming → complete)
 * - mount: Start streaming (new stream or deferred mount after orchestrator sets positions)
 * - update: New content arrived for current stream
 *
 * Stream phases: idle → mounting → streaming → finalizing → idle
 *
 * @returns The action to take, or null if no action needed
 */
export function detectRequiredAction(
	sessionState: SessionContextPluginState | undefined,
	prevSessionState: SessionContextPluginState | undefined,
	streamingState: PurePmInEditorDirectStreamingPluginState,
	autoAcceptAIChanges: boolean,
): StreamingActionType | null {
	if (!sessionState) {
		return null;
	}

	// === DERIVE SESSION TRANSITION INFO ===
	const session = {
		id: sessionState.sessionId,
		status: sessionState.status,
		responseVersion: sessionState.responseVersion,
		changed: sessionState.sessionId !== prevSessionState?.sessionId,
		cleared: !sessionState.sessionId && !!prevSessionState?.sessionId,
		justCompleted: sessionState.status === 'complete' && prevSessionState?.status === 'streaming',
		isStreaming: sessionState.status === 'streaming',
	};

	// === DERIVE STREAMING STATE INFO ===
	const streaming = {
		phase: streamingState.streamPhase,
		isIdle: streamingState.streamPhase === 'idle',
		isActive: streamingState.streamPhase !== 'idle',
		isProcessing:
			streamingState.streamPhase === 'mounting' || streamingState.streamPhase === 'streaming',
		canFinalize:
			streamingState.isStreaming &&
			!streamingState.hasFinalStream &&
			streamingState.streamPhase === 'streaming',
		hasNewContent: sessionState.responseVersion > streamingState.lastProcessedVersion,
	};

	// === DECISION TREE (in priority order) ===

	// 1. CLEANUP: Session changed while we were actively streaming
	//    → Need to reset before handling the new session
	if (session.changed && streaming.isActive && autoAcceptAIChanges) {
		return 'cleanup';
	}

	// 2. SESSION CLEARED: No active session anymore
	//    → Nothing to do
	if (session.cleared) {
		return null;
	}

	// 3. FINALIZE: Stream just completed and we're ready to finalize
	//    → Commit the final content
	if (session.justCompleted && streaming.canFinalize) {
		return 'finalize';
	}

	// 4. MOUNT: Session is streaming but we haven't started yet
	//    → Initialize streaming decorations
	//    Note: Also handles "deferred mount" when orchestrator updates positions
	if (session.isStreaming && (streaming.isIdle || streamingState.streamPhase === 'finalizing')) {
		return 'mount';
	}

	// 5. UPDATE: We're actively processing and have new content
	//    → Stream the new content into the document
	if (session.isStreaming && streaming.isProcessing && streaming.hasNewContent) {
		return 'update';
	}

	// No action needed
	return null;
}

/**
 * Checks if we should skip appendTransaction entirely.
 *
 * We can skip when nothing is happening (idle with no session) or
 * when streaming has completed and the session has ended.
 *
 * Note: We intentionally DON'T skip after mount - we need to process
 * pending content in the same transaction batch. State-based deduplication
 * (responseVersion, streamPhase) prevents infinite loops.
 */
export function shouldSkipAppendTransaction(
	streamingState: PurePmInEditorDirectStreamingPluginState,
	sessionState: SessionContextPluginState | undefined,
): boolean {
	const isIdle = streamingState.streamPhase === 'idle';
	const noActiveSession = !sessionState?.sessionId;
	const sessionIsIdle = !sessionState || sessionState.status === 'idle';

	// Skip if completely idle with no active session
	if (isIdle && sessionIsIdle && noActiveSession) {
		return true;
	}

	// Skip if stream finalized and session has ended
	const streamFinalized = streamingState.hasFinalStream;
	const sessionComplete = sessionState?.status === 'complete';

	if (streamFinalized && sessionComplete && noActiveSession) {
		return true;
	}

	return false;
}
